// Login.jsx

import React, { useState } from 'react';
import { Grid, TextField, Button, Typography, Box, Paper, Avatar, Link, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import LoginStyles from './Login.styles';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState(null);
  const [resetError, setResetError] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);
  const { login, checkEmailVerified, ROLES, resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleResetPassword = async () => {
    if (!resetEmail) {
      setResetError('Please enter your email address.');
      return;
    }
    setResetLoading(true);
    setResetMessage(null);
    setResetError(null);
    try {
      const result = await resetPassword(resetEmail);
      if (result.success) {
        setResetMessage('Password reset email sent. Please check your inbox.');
      } else {
        setResetError(result.error.message);
      }
    } catch (error) {
      setResetError('Failed to send password reset email.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      const isVerified = await checkEmailVerified(user);
      if (!isVerified) {
        navigate('/email-verification-pending');
        return;
      }

      switch (user.userType) {
        case ROLES.RENTER:
          navigate('/user-dashboard');
          break;
        case ROLES.AGENT:
        case ROLES.MANAGER:
          navigate('/agent-dashboard');
          break;
        default:
          navigate('/');
      }
    } catch (error) {
      console.error('Login failed:', error);
      alert('Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container sx={LoginStyles.container}>
      <Paper elevation={10} sx={LoginStyles.paper}>
        <Box display="flex" flexDirection="column" alignItems="center">
          {/* --- ADDED LOGO AT THE TOP --- */}
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/logo-new.png`}
            alt="Company logo"
            sx={LoginStyles.logo}
          />
          {/* -------------------------------- */}

          <Avatar sx={LoginStyles.avatar}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Login
          </Typography>

          <Box component="form" sx={LoginStyles.form} onSubmit={handleLogin}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              sx={LoginStyles.textField}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              autoComplete="current-password"
              sx={LoginStyles.textField}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={LoginStyles.loginButton}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Login'}
            </Button>

            <Typography variant="body2" sx={LoginStyles.registerText}>
              Not a user?{' '}
              <Link component={RouterLink} to="/register" sx={LoginStyles.registerLink}>
                Register here
              </Link>
            </Typography>

            <Typography variant="body2" sx={{ mt: 2 }}>
              <Link component="button" onClick={() => setOpenResetDialog(true)} sx={LoginStyles.registerLink}>
                Forgot Password?
              </Link>
            </Typography>
          </Box>

          <Dialog
            open={openResetDialog}
            onClose={() => {
              setOpenResetDialog(false);
              setResetEmail('');
              setResetMessage(null);
              setResetError(null);
            }}
          >
            <DialogTitle>Reset Password</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Email Address"
                type="email"
                fullWidth
                variant="standard"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
              />
              {resetMessage && <Alert severity="success" sx={{ mt: 2 }}>{resetMessage}</Alert>}
              {resetError && <Alert severity="error" sx={{ mt: 2 }}>{resetError}</Alert>}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenResetDialog(false)}>Cancel</Button>
              <Button onClick={handleResetPassword} disabled={resetLoading}>
                {resetLoading ? <CircularProgress size={24} /> : 'Send Reset Email'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Paper>
    </Grid>
  );
};

export default Login;
